import React, { useState } from "react";
import { Check, ExternalLink, Loader2, X, Zap } from "lucide-react";
import { toast } from "sonner";

import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/shared/components/ui/dialog";
import { ApiError } from "@/shared/api/errors";
import {
  useCreateMcpServer, useDeleteMcpServer, useLinkMcpServer, useMcpServer, useMcpServers,
  useMcpSignIn, usePatchMcpServer, useTestMcpServer,
} from "./hooks/useMcpServers";

import type { McpAuthType, McpServer, McpServerAuth, McpToolSummary, McpTransport } from "@/shared/types";

/* Add or edit one MCP server (backend/modules/mcp/README.md). Remote only: HTTP or SSE.

   The test is the gate. Add stays disabled until a test with exactly what is on
   screen has passed, and the API probes again on save. OAuth has no separate
   test: signing in lists the tools, and nothing is saved until it has. A test
   that answers "sign in" (2060002) switches the form to OAuth. */

const TRANSPORTS: { id: McpTransport; label: string; hint: string; placeholder: string }[] = [
  { id: "http", label: "HTTP", hint: "Streamable HTTP — what current servers speak.", placeholder: "https://mcp.example.com/mcp" },
  { id: "sse", label: "SSE", hint: "The older Server-Sent Events transport.", placeholder: "https://mcp.example.com/sse" },
];

const AUTHS: { id: McpAuthType; label: string; hint: string }[] = [
  { id: "none", label: "None", hint: "The server needs no credentials." },
  { id: "bearer", label: "Token", hint: "Sent as Authorization: Bearer <token>." },
  { id: "api_key", label: "API key", hint: "Sent in the header the server asks for." },
  { id: "oauth", label: "OAuth", hint: "Sign in on the server's own page. Signing in tests the connection." },
];

const OAUTH_REQUIRED = 2060002;
const CLIENT_ID_REQUIRED = 3120004;

type Test = { state: "idle" | "testing" } | { state: "ok"; tools: McpToolSummary[] } | { state: "fail"; message: string };

const inputCls =
  "w-full h-9 px-3 rounded-[8px] bg-[var(--surface)] ring-1 ring-[var(--hairline-strong)] text-[14px] outline-none focus:ring-[var(--hairline-focus)] placeholder:text-[var(--text-quaternary)] tracking-[-0.005em]";

function Segmented<T extends string>({ options, value, onChange }: {
  options: { id: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div className="grid gap-[3px] p-[3px] rounded-[9px] bg-[var(--surface-sunk)] ring-1 ring-inset ring-[var(--hairline)]" style={{ gridTemplateColumns: `repeat(${options.length}, minmax(0, 1fr))` }}>
      {options.map((o) => (
        <button
          key={o.id}
          type="button"
          onClick={() => onChange(o.id)}
          className={`press h-[26px] rounded-[6px] text-[12.5px] transition-colors ${
            value === o.id
              ? "bg-[var(--surface)] text-[var(--text-primary)] shadow-[0_1px_2px_rgba(0,0,0,0.06)] ring-1 ring-[var(--hairline-strong)]"
              : "text-[var(--text-tertiary)] hover:text-[var(--text-secondary)]"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function Field({ label, optional, children }: { label: string; optional?: boolean; children?: React.ReactNode }) {
  return (
    <div>
      <div className="section-label mb-1.5">
        {label}{optional && <span className="normal-case font-normal text-[var(--text-quaternary)]"> · optional</span>}
      </div>
      {children}
    </div>
  );
}

function ToolList({ tools }: { tools: McpToolSummary[] }) {
  return (
    <div className="max-h-[92px] overflow-y-auto flex flex-wrap gap-1">
      {tools.map((t) => (
        <span key={t.name} title={t.description} className="h-[18px] px-[6px] rounded-[5px] bg-[var(--surface-2)] text-[11.5px] font-mono text-[var(--text-secondary)] flex items-center">
          {t.name}
        </span>
      ))}
    </div>
  );
}

const messageOf = (e: unknown) => (e instanceof Error ? e.message : "Something went wrong.");

export function McpServerDialog({ agentId, server, linked, onOpenChange }: {
  agentId: string;
  /** Edit this one; absent, add a new server or mount one from the workspace. */
  server?: McpServer;
  /** Server ids already on this agent — the workspace picker leaves them out. */
  linked: string[];
  onOpenChange: (open: boolean) => void;
}) {
  const [name, setName] = useState(server?.name ?? "");
  const [url, setUrl] = useState(server?.url ?? "");
  const [transport, setTransport] = useState<McpTransport>(server?.transport ?? "http");
  const [auth, setAuth] = useState<McpAuthType>(server?.auth.type ?? "none");
  const [token, setToken] = useState("");
  const [headerName, setHeaderName] = useState(server?.auth.header_name ?? "X-API-Key");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [signInDetected, setSignInDetected] = useState(false);
  const [test, setTest] = useState<Test>({ state: "idle" });
  const [fromWorkspace, setFromWorkspace] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const workspace = useMcpServers();
  const detail = useMcpServer(server?.id ?? null);
  const tester = useTestMcpServer();
  const create = useCreateMcpServer();
  const patch = usePatchMcpServer();
  const remove = useDeleteMcpServer();
  const link = useLinkMcpServer();
  const close = (message?: string) => { if (message) toast.success(message); onOpenChange(false); };
  const signIn = useMcpSignIn((saved) => close(`${saved.name} connected · ${saved.tool_count} tools`));

  const available = (workspace.data ?? []).filter((s) => !linked.includes(s.id));
  const transportDef = TRANSPORTS.find((t) => t.id === transport)!;
  const authDef = AUTHS.find((a) => a.id === auth)!;
  const storedHint = server?.auth.type === auth ? server.auth.hint : undefined;
  const keepsToken = !!server && server.auth.type === auth && !token.trim();
  const connectionEdited = !server || url.trim() !== server.url || transport !== server.transport
    || auth !== server.auth.type || !!token.trim() || (auth === "api_key" && headerName !== server.auth.header_name);
  const credential: McpServerAuth = auth === "oauth" || auth === "none"
    ? { type: "none" }
    : { type: auth, ...(token.trim() ? { token: token.trim() } : {}), ...(auth === "api_key" ? { header_name: headerName.trim() } : {}) };
  // an OAuth server is renamed without signing in again
  const renamed = !!server && name.trim() !== server.name;
  const missingToken = (auth === "bearer" || auth === "api_key") && !token.trim() && !keepsToken;
  const saving = create.isPending || patch.isPending;
  const canSave = !!name.trim() && !!url.trim() && !missingToken && !saving
    && (test.state === "ok" || (!!server && !connectionEdited));
  const writeError = create.error ?? patch.error ?? link.error ?? remove.error ?? signIn.error;

  // anything that changes what the test talked to makes its answer stale
  const edit = <T,>(set: (v: T) => void) => (v: T) => { set(v); setTest({ state: "idle" }); };

  const runTest = () => {
    setTest({ state: "testing" });
    tester.mutate(
      { url: url.trim(), transport, auth: credential, ...(server ? { server_id: server.id } : {}) },
      {
        onSuccess: (result) => setTest({ state: "ok", tools: result.tools }),
        onError: (e) => {
          if (e instanceof ApiError && e.code === OAUTH_REQUIRED) {
            setAuth("oauth");
            setSignInDetected(true);
            setTest({ state: "idle" });
            return;
          }
          setTest({ state: "fail", message: messageOf(e) });
        },
      },
    );
  };

  const save = () => {
    if (!canSave) return;
    if (!server) {
      const body = { name: name.trim(), url: url.trim(), transport, auth: credential, agent_id: agentId };
      create.mutate(body, { onSuccess: (s) => close(`${s.name} added · ${s.tool_count} tools`) });
      return;
    }
    const body = connectionEdited
      ? { name: name.trim(), url: url.trim(), transport, auth: credential }
      : { name: name.trim() };
    patch.mutate({ id: server.id, body }, { onSuccess: () => close("Saved") });
  };

  const connect = () => {
    if (!name.trim() || !url.trim()) return;
    signIn.start({
      name: name.trim(), url: url.trim(), transport,
      ...(server ? { server_id: server.id } : { agent_id: agentId }),
      ...(clientId.trim() ? { client_id: clientId.trim(), client_secret: clientSecret.trim() || undefined } : {}),
    });
  };
  const showClient = !!clientId || (signIn.error instanceof ApiError && signIn.error.code === CLIENT_ID_REQUIRED);

  const tools = test.state === "ok" ? test.tools : detail.data?.tools;
  const signingIn = signIn.phase !== "idle";

  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="p-0 gap-0 w-[460px] max-w-[94vw] rounded-[16px] overflow-hidden bg-[var(--canvas)] border-[var(--hairline-strong)] shadow-dialog">
        <div className="px-5 pt-5 pb-4">
          <DialogTitle className="text-[15px] font-semibold tracking-[-0.01em] text-[var(--text-primary)]">
            {server ? `Configure ${server.name}` : "Add MCP server"}
          </DialogTitle>
          <DialogDescription className="text-[13px] text-[var(--text-tertiary)] mt-0.5">
            {server
              ? "Changes apply to every agent this server is on. A new URL or credential is tested before it is saved."
              : "Give this agent the tools of a remote Model Context Protocol server."}
          </DialogDescription>
        </div>

        {!server && available.length > 0 && (
          <div className="px-5 pb-3">
            <Segmented
              options={[{ id: "new", label: "New server" }, { id: "workspace", label: `From workspace · ${available.length}` }]}
              value={fromWorkspace ? "workspace" : "new"}
              onChange={(v) => setFromWorkspace(v === "workspace")}
            />
          </div>
        )}

        {fromWorkspace ? (
          <div className="px-5 pb-5 grid gap-[1px]">
            {available.map((s) => (
              <div key={s.id} className="flex items-center gap-2.5 px-2 py-[7px] rounded-[8px] hover:bg-[var(--surface)] min-w-0">
                <span className="min-w-0 flex-1">
                  <span className="block text-[13px] font-medium text-[var(--text-primary)] truncate">{s.name}</span>
                  <span className="block text-[11.5px] font-mono text-[var(--text-quaternary)] truncate">{s.url}</span>
                </span>
                <span className="shrink-0 text-[11px] font-mono tabular-nums text-[var(--text-tertiary)]">{s.tool_count} tools</span>
                <button
                  onClick={() => link.mutate({ id: s.id, agentId }, { onSuccess: () => close(`${s.name} added to this agent`) })}
                  disabled={link.isPending}
                  className="press shrink-0 h-[26px] px-2.5 rounded-[7px] ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[12.5px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] disabled:opacity-40"
                >
                  Add
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-5 pb-5 space-y-4">
            <Field label="Name">
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Linear" className={inputCls} />
            </Field>

            <Field label="Transport">
              <Segmented options={TRANSPORTS} value={transport} onChange={edit(setTransport)} />
              <div className="text-[11.5px] text-[var(--text-quaternary)] mt-1.5">{transportDef.hint}</div>
            </Field>

            <Field label="Server URL">
              <input value={url} onChange={(e) => edit(setUrl)(e.target.value)} placeholder={transportDef.placeholder} spellCheck={false} className={`${inputCls} font-mono text-[13px]`} />
            </Field>

            <Field label="Authentication">
              <Segmented options={AUTHS} value={auth} onChange={edit(setAuth)} />
              <div className="text-[11.5px] text-[var(--text-quaternary)] mt-1.5">{authDef.hint}</div>
            </Field>

            {auth === "api_key" && (
              <Field label="Header name">
                <input value={headerName} onChange={(e) => edit(setHeaderName)(e.target.value)} spellCheck={false} className={`${inputCls} font-mono text-[13px]`} />
              </Field>
            )}
            {(auth === "bearer" || auth === "api_key") && (
              <Field label={auth === "bearer" ? "Token" : "API key"}>
                <input
                  type="password" autoComplete="off" value={token} onChange={(e) => edit(setToken)(e.target.value)}
                  placeholder={storedHint ? `Leave empty to keep ${storedHint}` : "Paste it here"}
                  className={`${inputCls} font-mono text-[13px]`}
                />
                <div className="text-[11.5px] text-[var(--text-quaternary)] mt-1">Stored encrypted, never shown to the model.</div>
              </Field>
            )}
            {auth === "oauth" && showClient && (
              <Field label="Client ID">
                <input value={clientId} onChange={(e) => setClientId(e.target.value)} placeholder="From the server's developer settings" spellCheck={false} className={`${inputCls} font-mono text-[13px]`} />
                <input type="password" autoComplete="off" value={clientSecret} onChange={(e) => setClientSecret(e.target.value)} placeholder="Client secret (optional)" className={`${inputCls} font-mono text-[13px] mt-2`} />
                <div className="text-[11.5px] text-[var(--text-quaternary)] mt-1 break-all">
                  Callback URL to register: <span className="font-mono">{window.location.origin}/oauth/mcp/callback</span>
                </div>
              </Field>
            )}

            {tools && tools.length > 0 && (
              <Field label={`${tools.length} tools`}>
                <ToolList tools={tools} />
              </Field>
            )}
          </div>
        )}

        {!fromWorkspace && (
          <div className="px-5 py-3.5 border-t hairline bg-[var(--surface)] space-y-2">
            {signIn.phase === "blocked" && signIn.authorizationUrl && (
              <a href={signIn.authorizationUrl} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 text-[12.5px] text-[var(--info-text)]">
                <ExternalLink className="w-3 h-3" strokeWidth={2} />The popup was blocked — open the sign-in page
              </a>
            )}
            {(writeError || test.state === "fail") && (
              <div className="text-[12.5px] leading-[1.5] text-[var(--danger)]">
                {test.state === "fail" ? test.message : messageOf(writeError)}
              </div>
            )}
            <div className="flex items-center justify-between gap-3">
              {auth === "oauth" ? (
                <span className="text-[12px] text-[var(--text-tertiary)] min-w-0 truncate">
                  {signIn.phase === "waiting" || signIn.phase === "blocked" ? "Waiting for the sign-in to finish…"
                    : signIn.phase === "finishing" ? "Listing the server's tools…"
                    : signInDetected ? "This server uses sign-in." : ""}
                </span>
              ) : (
                <button
                  onClick={runTest}
                  disabled={!url.trim() || missingToken || test.state === "testing"}
                  className={`press h-[30px] px-3 rounded-[8px] text-[13px] font-medium flex items-center gap-1.5 min-w-0 disabled:opacity-40 disabled:pointer-events-none transition-colors ${
                    test.state === "ok" ? "bg-[var(--success-bg)] text-[var(--success)] ring-1 ring-[var(--success-ring)]"
                      : test.state === "fail" ? "bg-[var(--danger-bg)] text-[var(--danger)] ring-1 ring-[var(--danger-ring)]"
                      : "ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]"
                  }`}
                >
                  {test.state === "testing" ? <><Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />Testing…</>
                    : test.state === "ok" ? <><Check className="w-3 h-3" strokeWidth={2.5} /><span className="truncate">Connected · {test.tools.length} tools</span></>
                    : test.state === "fail" ? <><X className="w-3 h-3" strokeWidth={2.5} /><span className="truncate">Failed — retry</span></>
                    : <><Zap className="w-3 h-3" strokeWidth={2} />Test connection</>}
                </button>
              )}
              <div className="flex items-center gap-2 shrink-0">
                {signingIn ? (
                  <button onClick={signIn.cancel} className="press h-[30px] px-3 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]">
                    Cancel sign-in
                  </button>
                ) : (
                  <button onClick={() => onOpenChange(false)} className="press h-[30px] px-3 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)]">
                    Cancel
                  </button>
                )}
                {auth === "oauth" && renamed && !connectionEdited && (
                  <button onClick={save} disabled={saving} className="press h-[30px] px-3 rounded-[8px] ring-1 ring-[var(--hairline-strong)] bg-[var(--canvas)] text-[13px] text-[var(--text-secondary)] hover:bg-[var(--accent-hover)] disabled:opacity-40">
                    Save name
                  </button>
                )}
                {auth === "oauth" ? (
                  <button onClick={connect} disabled={!name.trim() || !url.trim() || signingIn} className="press h-[30px] px-3.5 rounded-[8px] bg-[var(--brand)] text-[var(--brand-foreground)] text-[13px] font-medium hover:bg-[var(--brand-hover)] disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5">
                    {signingIn && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
                    {server?.auth.type === "oauth" ? "Reconnect" : "Connect with OAuth"}
                  </button>
                ) : (
                  <button onClick={save} disabled={!canSave} title={canSave ? undefined : "Test the connection first"} className="press h-[30px] px-3.5 rounded-[8px] bg-[var(--brand)] text-[var(--brand-foreground)] text-[13px] font-medium hover:bg-[var(--brand-hover)] disabled:opacity-40 disabled:pointer-events-none flex items-center gap-1.5">
                    {saving && <Loader2 className="w-3 h-3 animate-spin" strokeWidth={2.25} />}
                    {server ? "Save changes" : "Add server"}
                  </button>
                )}
              </div>
            </div>
            {server && (
              <div className="pt-1 text-[12px]">
                {confirmDelete ? (
                  <span className="text-[var(--text-secondary)]">
                    Delete {server.name} from the workspace? Every agent loses its tools.{" "}
                    <button onClick={() => remove.mutate(server.id, { onSuccess: () => close(`${server.name} deleted`) })} className="font-medium text-[var(--danger)] hover:underline">Delete</button>{" · "}
                    <button onClick={() => setConfirmDelete(false)} className="hover:underline">Keep</button>
                  </span>
                ) : (
                  <button onClick={() => setConfirmDelete(true)} className="text-[var(--text-tertiary)] hover:text-[var(--danger)]">Delete from workspace…</button>
                )}
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
